/**
 * PBX-config validators and normalizer — Phase 3.2.c.
 *
 * Pulled out of admin.js so the channel-settings + shift-management
 * view modules can `import` them directly instead of receiving them
 * via `configure({helpers})`.
 *
 * Pure functions (no admin.js state). `getRegisteredPbxTrunks` stays
 * in admin.js because it reaches into the shift-management view state
 * and `ADMIN_ME`; once 3.2.d resolves cross-view imports it can move
 * here.
 */

import { DEFAULT_PBX_EVENTS } from './channels.js';

export function isValidTimeString(value) {
  return typeof value === 'string' && /^\d{2}:\d{2}$/.test(value);
}

export function normalizePbxConfig(rawConfig) {
  const next = Object.assign({}, rawConfig || {});

  // Connection scheme. Only normalize keys that are present so we never inject
  // a default that would override the backend's behaviour for untouched configs
  // (the backend derives http/https + verify from base_url / use_https).
  if (typeof next.base_url === 'string') {
    next.base_url = next.base_url.trim();
    if (!next.base_url) delete next.base_url;
  } else if (next.base_url != null) {
    delete next.base_url;
  }
  if ('use_https' in next) next.use_https = Boolean(next.use_https);
  if ('verify_ssl' in next) next.verify_ssl = Boolean(next.verify_ssl);

  const rawEvents = Array.isArray(next.event_codes) ? next.event_codes : [];
  const cleanedEvents = rawEvents
    .map((code) => (code == null ? '' : String(code).trim()))
    .filter(Boolean);
  next.event_codes = cleanedEvents.length ? cleanedEvents : [...DEFAULT_PBX_EVENTS];
  const retention = Number(next.recording_retention_days);
  next.recording_retention_days = Number.isFinite(retention) && retention > 0 ? Math.round(retention) : 365;
  const start = typeof next.staffing_start === 'string' ? next.staffing_start : '';
  const end = typeof next.staffing_end === 'string' ? next.staffing_end : '';
  next.staffing_start = isValidTimeString(start) ? start : '09:00';
  next.staffing_end = isValidTimeString(end) ? end : '23:00';

  const trunkMap = new Map();
  const pushTrunk = (name, phone) => {
    if (!name) return;
    const trimmed = String(name).trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (trunkMap.has(key)) return;
    const entry = { name: trimmed };
    if (phone) {
      const phoneText = String(phone).trim();
      if (phoneText) entry.def_outbound_cid = phoneText;
    }
    trunkMap.set(key, entry);
  };

  if (Array.isArray(next.staffing_trunk_details)) {
    next.staffing_trunk_details.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      pushTrunk(item.name, item.def_outbound_cid || item.phone);
    });
  }

  if (Array.isArray(next.staffing_trunks)) {
    next.staffing_trunks.forEach((item) => {
      if (!item) return;
      if (typeof item === 'object') {
        pushTrunk(item.name, item.def_outbound_cid || item.phone);
      } else {
        pushTrunk(item, null);
      }
    });
  }

  const trunks = Array.from(trunkMap.values());
  next.staffing_trunks = trunks.map((entry) => entry.name);
  if (trunks.length) {
    next.staffing_trunk_details = trunks;
  } else if (next.staffing_trunk_details) {
    delete next.staffing_trunk_details;
  }

  if (next.default_lines_by_role) {
    delete next.default_lines_by_role;
  }

  if (typeof next.staffing_trunks_synced_at === 'string') {
    const parsed = Date.parse(next.staffing_trunks_synced_at);
    if (Number.isFinite(parsed)) {
      next.staffing_trunks_synced_at = new Date(parsed).toISOString();
    } else {
      delete next.staffing_trunks_synced_at;
    }
  } else if (next.staffing_trunks_synced_at) {
    delete next.staffing_trunks_synced_at;
  }

  return next;
}
